import Auth from '@@types/Auth';
import { ApproveBoolean, EffectiveFeatureName, EffectiveFeaturesResponse } from '@@types/EffectiveFeatures';
import { Organization } from '@@types/Organization';
import { Tenant } from '@@types/Tenant';

import { AbsenceType, GNB_TABS } from '~/constants';
import { HDC_TENANT_ID, NEQA_DEV_TENANT_ID, NHN_TENANT_IDS } from '~/constants/tenantId';
import ChCookie from '~/services/ChCookie';
import GA from '~/services/GA/GA';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import environment from '~/stores/providers/environment';
import { getQueryParameterByName, isNative } from '~/utils/runEnvironmentUtil';
import i18n from 'i18next';
import { action, computed, observable } from 'mobx';
import moment from 'moment-timezone';
import equals from 'ramda/es/equals';
import includes from 'ramda/es/includes';
import pipe from 'ramda/es/pipe';
import prop from 'ramda/es/prop';
import propOr from 'ramda/es/propOr';
import reject from 'ramda/es/reject';
import sortBy from 'ramda/es/sortBy';
import values from 'ramda/es/values';
import when from 'ramda/es/when';

import { ids as rejectGATenantIds } from '../services/GA/rejectTenantList.json';
import { api } from './providers/Api2';
import { interTenantApi } from './providers/InterTenantApi';

const tabOrder = {
  [GNB_TABS.ORGANIZATION_CHART]: 0,
  [GNB_TABS.FAVORITE]: 1,
  [GNB_TABS.RECENT]: 2,
};

class UserStore {
  @observable
  id = '';

  @observable
  orgId = '';

  @observable
  tenantId = '';

  @observable
  lastOnlineAt = 0;

  @observable
  lastChannelId = '';

  @observable
  enabledNewFeature = false;

  @observable
  enabledShareChannelFeature = false;

  @observable
  isInterTenantMeFetchError = false;

  @observable
  accessToken = '';

  @observable
  websocketUrl = '';

  @observable
  organization = {};

  @observable
  tenantMemberRole = '';

  @observable
  isUpdatingLocaleInfo = true;

  @observable
  isWorking = false;

  @observable
  meetingId = '';

  @observable
  webexId = '';

  @action
  setIsWorking = (val) => {
    this.isWorking = val;
  };

  @action
  setUpdateingLocaleInfo = (value: boolean) => {
    this.isUpdatingLocaleInfo = value;
  };

  setIntlLocale = (locale: string) => {
    i18n.changeLanguage(locale);
    window.__localeId__ = locale; // for date-fns
    moment.locale(locale);
    const htmlNode = document.querySelector('html');
    htmlNode?.setAttribute?.('lang', locale);
  };

  @observable
  timezone = '';

  @observable
  idProviderId = '';

  @observable
  tenant: Tenant = null;

  @observable
  hasIpPhone = false;

  @observable
  organizationCount = 0;

  @observable
  disableOrganizationChart = false;

  @observable
  vacation = {
    '@type': 'CommonVacation',
    displayProfileFlag: false,
    autoReplyMailFlag: false,
    type: AbsenceType.HIDDEN,
    startedAt: '', // 2019-11-10T00:00:00+09:00
    endedAt: '', // 2019-11-10T00:00:00+09:00
    message: '',
    flags: ['reply_from_internal_mail', 'reply_from_external_mail'],
  };

  @observable
  effectiveFeatures: Record<EffectiveFeatureName, ApproveBoolean> | null = null;

  @computed
  get canUseInterTenant() {
    return this.enabledShareChannelFeature && !this.isInterTenantMeFetchError;
  }

  @computed
  get isOwner(): boolean {
    return this.tenantMemberRole === 'owner';
  }

  @computed
  get isAdmin(): boolean {
    return this.tenantMemberRole === 'admin';
  }

  @computed
  get isGuest(): boolean {
    return this.tenantMemberRole === 'guest';
  }

  @computed
  get isSso(): boolean {
    return this.idProviderId !== '5';
  }

  @computed
  get isIpPhoneAvailable(): boolean {
    return this.hasIpPhone && isNative() && ChCookie.get('messengerCheckInternal') === '1';
  }

  get useReaction(): boolean {
    return true;
  }

  @computed
  get tabs() {
    return pipe(
      values,
      when(
        () => this.isNHNTenant || this.isGuest || this.disableOrganizationChart,
        (strList: string[]) => reject(equals<string>(GNB_TABS.ORGANIZATION_CHART), strList)
      ),
      sortBy((tab) => tabOrder[tab])
    )(GNB_TABS);
  }

  @computed
  get isNHNTenant() {
    return NHN_TENANT_IDS.includes(this.tenantId);
  }

  @computed
  get isHDCTenant() {
    return this.tenantId === HDC_TENANT_ID || this.tenantId === NEQA_DEV_TENANT_ID;
  }

  @action
  setIsInterTenantMeFetchError = (value: boolean) => {
    this.isInterTenantMeFetchError = value;
  };

  fetchAndCache = async () => {
    const auth = await api.common.fetchAuth();
    await globalSettingStore.cacheOrFetchEnv();
    auth.enabledNewFeature = this.fixEnabledNewFeature(auth.enabledNewFeature);
    auth.enabledShareChannelFeature = this.fixEnabledShareChannelFeature(auth.enabledShareChannelFeature);
    if (auth.enabledShareChannelFeature) {
      try {
        const result = await interTenantApi.common.fetchAuth();
        this.setIsInterTenantMeFetchError(false);
        const { lastChannelId, lastChannelAccessedAt } = result;
        auth.lastChannelId =
          (lastChannelAccessedAt || 0) > (auth.lastChannelAccessedAt || 0) ? lastChannelId : auth.lastChannelId;
      } catch (e) {
        this.setIsInterTenantMeFetchError(true);
      }
    }
    this.setAuth(auth);
    return auth;
  };

  fetchIpPhoneStatus = () => {
    return api.phone.checkIpPhone().then(propOr(false, 'ipPhone')).then(this.setHasIpPhone);
  };

  fetchTenantOrganizationCount = () => {
    return api.organization.fetchTenantOrganizationCount().then(prop('totalCount')).then(this.setOrganizationCount);
  };

  fetchOrganization = async (orgId: string) => {
    const organization = await api.organization.fetchOrganizationById(orgId);
    this.setOrganization(organization);
  };

  fetchEffectiveFeatures = () => {
    return api.me.fetchEffectiveFeatures().then(this.setEffectiveFeatures);
  };

  isNonUserLog = (log) => {
    return this.id !== log.senderId;
  };

  @action
  setDisableOrganizationChart = (disableOrganizationChart) => {
    this.disableOrganizationChart = disableOrganizationChart;
  };

  @action
  setDefaultTenant = (tenant) => {
    this.tenant = tenant;
  };

  @action
  setHasIpPhone = (hasIpPhone) => {
    this.hasIpPhone = hasIpPhone;
  };

  @action
  setOrganizationCount = (count) => {
    this.organizationCount = count;
  };

  @action
  setEffectiveFeatures = (features: EffectiveFeaturesResponse[]) => {
    this.effectiveFeatures = features.reduce((map, feature) => {
      map[feature.name] = feature.serviceUi && (feature.approval ? 'approve' : true);
      return map;
    }, {}) as Record<EffectiveFeatureName, ApproveBoolean>;
  };

  @action
  setAuth = (auth: Auth) => {
    const {
      id,
      enabledNewFeature,
      enabledShareChannelFeature,
      lastChannelId,
      lastOnlineAt,
      orgId,
      tenantId,
      accessToken,
      websocketUrl,
      meetingId,
      webexId,
    } = auth;
    this.id = id;
    this.enabledNewFeature = enabledNewFeature;
    this.enabledShareChannelFeature = enabledShareChannelFeature;
    // lastChannelId 가 설정되지 않는 경우 BootstrapActions에서 가장 최상단 채널을 선택하게 fallback 처리 있음
    this.lastChannelId = lastChannelId;
    this.lastOnlineAt = lastOnlineAt;
    this.orgId = orgId;
    this.tenantId = tenantId;
    this.accessToken = accessToken;
    this.websocketUrl = websocketUrl;
    this.meetingId = meetingId || '';
    this.webexId = webexId || '';
    if (
      !globalSettingStore.isGov &&
      !globalSettingStore.env.isClosedNetwork &&
      !includes(tenantId, rejectGATenantIds)
    ) {
      GA.initializeGA(auth, environment.gaId);
    }
    GA.setFieldsObject({ id });
  };

  @action
  setTimezone = (timezone) => {
    moment.tz.setDefault(timezone);
    window.__timezoneId__ = timezone; // for date-fns
    this.timezone = timezone;
  };

  setOrganization = (organization: Organization) => {
    this.organization = organization;
  };

  @action
  setTenantMemberRole = (role: string) => {
    this.tenantMemberRole = role;
  };

  @action
  setIdProviderId = (idProviderId: string) => {
    this.idProviderId = idProviderId;
  };

  private fixEnabledNewFeature = (enabledNewFeature: boolean) => {
    const enfParam = getQueryParameterByName('_enf');
    return enfParam !== null ? enfParam === 'true' : enabledNewFeature;
  };

  private fixEnabledShareChannelFeature = (newFeature: boolean) => {
    const enfParam = getQueryParameterByName('_share');
    return enfParam !== null ? enfParam === 'true' : newFeature;
  };
}

export default UserStore;

export const userStore = new UserStore();
